import { LinearLoader } from "../../../components/linear-loader/LinearLoader.jsx";
import { useEffect } from "react";
import { useSellerPageContext } from "../../../context-state/SellerPageContext.jsx";
import { useNavigate } from "react-router-dom";
import { ErrorService } from "../../../services/errorService.js";
import * as API from "../../../api/index.js";
import { isFutureEvent } from "../../../utils/sort.js";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { getEndUserTokenKeyByHandler } from "../../../utils/endUser.js";
import { preloadFonts } from "../../../utils/fontLoader.js";
import { getMarketplaceLoader } from "../../../utils/market.js";
import { notifyFlutterUserStatus } from "../../../utils/flutterBridge.js";
import { updateI18nCurrency } from "../../../locales/i18n.js";
import { filterValidSubscriptions, getValidHrcCode } from "../../../utils/subscription.js";

export const SellerPublicPageGuard = ({ children }) => {
    const MarketplaceLoader = getMarketplaceLoader()
    const navigate = useNavigate();
    const navigateHome = () => navigate('/', { replace: true });
    const { accountContext, eventContext, subscriptionContext, endUserContext } = useSellerPageContext()
    const { setAccount } = accountContext
    const { endUser, setEndUser, setPurchasedSubscriptions, setPurchasedEvents, triggerEndUserDataFetch, fetchingEndUserData, setFetchingEndUserData } = endUserContext
    const { setEvents } = eventContext
    const { setSubscriptions } = subscriptionContext
    const [isLoading, setIsLoading] = useState(true)
    const { handler: sellerHandler } = useParams()

    const getPublicSellerPageData = async () => {
        if (!sellerHandler) {
            return
        }
        try {
            const { data } = await API.getAccountIdByHandler(sellerHandler)
            setAccount(data)

            // Update i18n currency based on seller's currency
            if (data?.currency) {
                updateI18nCurrency(data.currency)
            }
        } catch (e) {
            console.log(e);
            ErrorService.handleError(e)
            return
        }
    }

    const getEndUserData = async () => {
        const token = localStorage.getItem(getEndUserTokenKeyByHandler(sellerHandler))
        localStorage.removeItem('endUserId')
        localStorage.removeItem('hrcCode')
        if (!token) {
            // Notify Flutter that user is NOT logged in
            notifyFlutterUserStatus(false)
            return
        }

        try {
            setFetchingEndUserData(true)
            const { data } = await API.getEndUserDataByTokenAndSellerHandler({ token, sellerHandler })
            setEndUser(data.endUser)
            // sort the events by start date
            setPurchasedEvents(data.userEvents.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)))
            // sort by startDate date - show subscriptions with active status for My Account
            const activeStatusSubscriptions = data.seasonTickets
                .filter(sub => sub.status === 'active')
                .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
            setPurchasedSubscriptions(activeStatusSubscriptions)

            // Filter ONLY for HRC selection - valid subscriptions
            const validSubscriptions = filterValidSubscriptions(data.seasonTickets);
            const hrc = getValidHrcCode(validSubscriptions, data.endUser.selectedHrc);

            // for the event page
            localStorage.setItem('endUserId', data.endUser._id)
            localStorage.setItem('hrcCode', hrc)
            // Notify Flutter and parent window that user IS logged in
            notifyFlutterUserStatus(true, data.endUser)
        } catch (e) {
            console.log(e);
            // If error fetching user data, notify Flutter user is not logged in
            notifyFlutterUserStatus(false)
        } finally {
            setFetchingEndUserData(false)
        }
    }

    const loadFonts = async () => {
        try {
            // Preload fonts if they exist in the account design
            if (accountContext.account?.design) {
                const fontsToLoad = []
                if (accountContext.account.design.sellerPageFont && accountContext.account.design.sellerPageFontUrl) {
                    fontsToLoad.push({
                        family: accountContext.account.design.sellerPageFont,
                        url: accountContext.account.design.sellerPageFontUrl
                    })
                }
                if (accountContext.account.design.sellerPageTitleFont && accountContext.account.design.sellerPageTitleFontUrl) {
                    fontsToLoad.push({
                        family: accountContext.account.design.sellerPageTitleFont,
                        url: accountContext.account.design.sellerPageTitleFontUrl
                    })
                }

                if (fontsToLoad.length > 0) {
                    await preloadFonts(fontsToLoad)
                }
            }
        } catch (error) {
            console.error('Failed to load fonts:', error)
            // Continue loading the page even if fonts fail to load
        }
    }

    useEffect(() => {
        const init = async () => {
            setIsLoading(true)
            await Promise.all([getPublicSellerPageData(), getEndUserData()])
            await loadFonts()
            setIsLoading(false)
        }
        init()
    }, []);

    useEffect(() => {
        if (triggerEndUserDataFetch) {
            if (triggerEndUserDataFetch.token) {
                localStorage.setItem(getEndUserTokenKeyByHandler(sellerHandler), triggerEndUserDataFetch.token)
            }
            getEndUserData()
        }
    }, [triggerEndUserDataFetch])

    return (
        !isLoading ? children : <MarketplaceLoader />
    )
}
